import { Shape, ShapeOptions } from "../Shape";
import { SVGParsedPathData, createSVGElement } from "../svg";
import { YZCanvasContext } from "../canvas";
import { length } from "../../../math";

export type BallShapeOptions = {
  ballRadius?: number; // 半径
} & ShapeOptions;

export class BallShape extends Shape {
  public ballRadius: number;

  constructor(options?: BallShapeOptions) {
    super(options);
    this.ballRadius = options?.ballRadius || 0;
  }

  protected cloneShape(): BallShape {
    const shape = new BallShape({
      ...super.getBaseOptions(),
      ballRadius: this.ballRadius
    });
    return shape;
  }

  // 供外部使用：基本属性变更
  public update(options: Partial<BallShapeOptions>) {
    if (typeof options.ballRadius == "number") {
      this.ballRadius = options.ballRadius;
      this.shouldUpdatePathD = true;
    }
  }

  public getBoundingClientRect() {
    return {
      x: -this.ballRadius,
      y: -this.ballRadius,
      width: this.ballRadius * 2,
      height: this.ballRadius * 2
    };
  }

  public toSVGEl(): SVGElement {
    const el = createSVGElement("circle");
    this.setAttr({
      cx: "0",
      cy: "0",
      r: String(this.ballRadius)
    });
    return el;
  }

  public toSVGPathData(): SVGParsedPathData[] {
    if (this.ballRadius) {
      return [
        ["m", 0, -this.ballRadius],
        [
          "a",
          this.ballRadius,
          this.ballRadius,
          0,
          1,
          1,
          0,
          2 * this.ballRadius
        ],
        [
          "a",
          this.ballRadius,
          this.ballRadius,
          0,
          1,
          1,
          0,
          -2 * this.ballRadius
        ],
        ["z"]
      ];
    } else {
      return [["m", 0, 0]];
    }
  }

  public toCanvasContext(ctx: YZCanvasContext) {
    ctx.beginPath();
    ctx.arc(0, 0, this.ballRadius, 0, Math.PI * 2, true);
    ctx.closePath();
  }

  public eq(ball: BallShape): boolean {
    return (
      this.x === ball.x &&
      this.y === ball.y &&
      this.ballRadius === ball.ballRadius &&
      this.mass === ball.mass
    );
  }

  public midPointBtw(ball: BallShape) {
    return {
      x: this.x + (ball.x - this.x) / 2,
      y: this.y + (ball.y - this.y) / 2
    };
  }

  public length() {
    return length(this.x, this.y);
  }

  public scalar(scale: number) {
    this.moveTo(this.x * scale, this.y * scale);
    return this;
  }

  public normalize(len = 1): BallShape {
    const totalLength = this.length();
    if (totalLength) {
      const scale = len / totalLength;
      this.scalar(scale);
    }
    return this;
  }

  public clamp(len: number) {
    const totalLength = this.length();
    if (totalLength && totalLength > len) {
      this.scalar(len / totalLength);
    }
    return this;
  }

  public cross(pt: { x: number; y: number }) {
    const x = this.x;
    const y = this.y;
    return {
      x: y * pt.x - x * pt.y,
      y: x * pt.y - y * pt.x
    };
  }

  public multiply(pt: { x: number; y: number }) {
    return {
      x: this.x * pt.x,
      y: this.y * pt.y
    };
  }

  public div(pt: { x: number; y: number }) {
    return {
      x: this.x / pt.x,
      y: this.y / pt.y
    };
  }

  public add(pt: { x: number; y: number }) {
    return {
      x: this.x + pt.x,
      y: this.y + pt.y
    };
  }

  public sub(pt: { x: number; y: number }) {
    return {
      x: this.x - pt.x,
      y: this.y - pt.y
    };
  }

  public dot(pt: { x: number; y: number }): number {
    return this.x * pt.x + this.y * pt.y;
  }

  public reflect(pt: { x: number; y: number }) {
    const dp = this.dot(pt);
    return {
      x: this.x - 2 * dp * pt.x,
      y: this.y - 2 * dp * pt.y
    };
  }
}

export default BallShape;
